import type { AiSubscriptionProvider } from './ai-subscriptions/providers';
import type {
  CodeSessionAiSubscriptionModel,
  CodeSessionCatalogModel,
  CodeSessionModel,
  CodeSessionReasoningEffort,
  CodeSessionSpeed,
} from './inference/catalog';

import {
  AI_SUBSCRIPTION_DESCRIPTORS,
  isAiSubscriptionProvider,
} from './ai-subscriptions/providers';
import {
  CODE_SESSION_AI_SUBSCRIPTION_MODELS,
  CODE_SESSION_ANTHROPIC_MODELS,
  CODE_SESSION_GPT_61_SOL_MODEL,
  CODE_SESSION_REASONING_EFFORTS,
  CODE_SESSION_ROUTED_MODELS,
  isOfferedCodeSessionModel,
} from './inference/catalog';

// Keep the catalog's public imports stable; selection and storage rules live here, definitions there.
export type {
  CodeSessionAiSubscriptionModel,
  CodeSessionCatalogModel,
  CodeSessionModel,
  CodeSessionReasoningEffort,
  CodeSessionSpeed,
} from './inference/catalog';
export {
  CODE_SESSION_AI_SUBSCRIPTION_MODELS,
  CODE_SESSION_ANTHROPIC_MODELS,
  CODE_SESSION_GLM_MODEL,
  CODE_SESSION_GPT_61_SOL_MODEL,
  CODE_SESSION_GPT_6_LUNA_MODEL,
  CODE_SESSION_OPENAI_MODELS,
  CODE_SESSION_REASONING_EFFORTS,
  CODE_SESSION_ROUTED_MODELS,
  CODE_SESSION_SPEEDS,
  isOfferedCodeSessionModel,
} from './inference/catalog';

/**
 * How sessions store a subscription model: `ai-subscription/<provider>/<provider model id>`.
 * The prefix is the route, so no other column can disagree with it. The migrations that back
 * subscription sessions in the database match the same prefix, and a test pins it.
 */
export const AI_SUBSCRIPTION_MODEL_PREFIX = 'ai-subscription/';

export const OPENCODE_GATEWAY_PROVIDER_ID = 'vercel';

/**
 * OpenCode addresses Gateway models through its `vercel` provider as `vercel/<creator>/<model>`:
 * a Gateway model is stored, and runs, as `vercel/<Gateway id>`.
 */
export const OPENCODE_GATEWAY_PROVIDER_PREFIX = `${OPENCODE_GATEWAY_PROVIDER_ID}/` as const;

/**
 * Whether a session's stored model runs on its owner's AI subscription. The
 * prefix decides, not the catalog, so a subscription model since removed from
 * the catalog still never runs on the Gateway.
 */
export function isAiSubscriptionModel(model: string): boolean {
  return model.startsWith(AI_SUBSCRIPTION_MODEL_PREFIX);
}

/**
 * How a stored model names the effort its session runs at: a `#<effort>` suffix after the model
 * and its speed, borrowed from OpenCode's `provider/model#variant` notation (`parseModelRef`). The
 * model reaches OpenCode without it (`resolveCodeSessionModel`). A stored model without one runs
 * at its model's catalog effort.
 */
const REASONING_EFFORT_SEPARATOR = '#';

function isCodeSessionReasoningEffort(value: string): value is CodeSessionReasoningEffort {
  return CODE_SESSION_REASONING_EFFORTS.some((effort) => effort === value);
}

/**
 * A stored model as the model it names and the effort its suffix names, if any. A suffix that
 * names no effort stays part of the model, which then matches nothing in the catalog.
 */
export function splitStoredModelEffort(model: string): {
  model: string;
  effort?: CodeSessionReasoningEffort;
} {
  const separator = model.lastIndexOf(REASONING_EFFORT_SEPARATOR);
  const effort = model.slice(separator + 1);
  return separator >= 0 && isCodeSessionReasoningEffort(effort)
    ? { model: model.slice(0, separator), effort }
    : { model };
}

/** `model`, as stored, naming `effort`. */
function storedWithEffort(model: string, effort: CodeSessionReasoningEffort): string {
  return `${model}${REASONING_EFFORT_SEPARATOR}${effort}`;
}

/**
 * How OpenCode names a model's fast twin: `<id>-fast`. OpenCode 2.0.26 makes one of each
 * models.dev `experimental.modes.fast`, which sends the base id as its model with
 * `service_tier: "priority"` for every plan model, and lists each plan model's twin, both as
 * `providers/chatgpt/__tests__/opencode-wire.integration.test.ts` records from the pinned binary.
 */
const OPENCODE_FAST_MODEL_SUFFIX = '-fast';

type SpeedSuffix<Speed extends CodeSessionSpeed> = Speed extends 'fast'
  ? typeof OPENCODE_FAST_MODEL_SUFFIX
  : '';

/** The provider a stored subscription model names, and the id after it that OpenCode runs. */
export type AiSubscriptionModelName = { provider: AiSubscriptionProvider; modelId: string };

/**
 * What a stored subscription model names, at whatever effort; undefined for a model of another
 * route, or one naming a provider this build doesn't know.
 */
export function aiSubscriptionModelOf(model: string): AiSubscriptionModelName | undefined {
  const { model: named } = splitStoredModelEffort(model);
  if (!isAiSubscriptionModel(named)) {
    return undefined;
  }
  const rest = named.slice(AI_SUBSCRIPTION_MODEL_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0) {
    return undefined;
  }
  const provider = rest.slice(0, slash);
  return isAiSubscriptionProvider(provider)
    ? { provider, modelId: rest.slice(slash + 1) }
    : undefined;
}

/**
 * What a stored subscription model names, for a server reader of a session row: a model naming no
 * provider this build knows is a fault there, since the rows and the registry disagree.
 */
export function aiSubscriptionModelOrThrow(model: string): AiSubscriptionModelName {
  const named = aiSubscriptionModelOf(model);
  if (named === undefined) {
    throw new Error(`The session's model names no known AI subscription provider: ${model}`);
  }
  return named;
}

function aiSubscriptionModelIdOf(
  entry: Pick<CodeSessionAiSubscriptionModel, 'providerModelId' | 'speed'>,
): string {
  return entry.speed === 'fast'
    ? `${entry.providerModelId}${OPENCODE_FAST_MODEL_SUFFIX}`
    : entry.providerModelId;
}

type StoredModel<Entry extends CodeSessionCatalogModel> =
  Entry extends CodeSessionAiSubscriptionModel
    ? `${typeof AI_SUBSCRIPTION_MODEL_PREFIX}${Entry['provider']}/${Entry['providerModelId']}${SpeedSuffix<Entry['speed']>}`
    : Entry extends CodeSessionModel
      ? `${typeof OPENCODE_GATEWAY_PROVIDER_PREFIX}${Entry['modelId']}`
      : never;

/**
 * How sessions store a model of the catalog at its catalog effort. Nothing else builds a stored
 * model; `withReasoningEffort` names another effort on one.
 */
export function storedModelOf<Entry extends CodeSessionCatalogModel>(
  entry: Entry,
): StoredModel<Entry> {
  const stored =
    entry.route === 'ai_subscription'
      ? `${AI_SUBSCRIPTION_MODEL_PREFIX}${entry.provider}/${aiSubscriptionModelIdOf(entry)}`
      : `${OPENCODE_GATEWAY_PROVIDER_PREFIX}${entry.modelId}`;
  return stored as StoredModel<Entry>;
}

/** The OpenCode model reference a subscription model runs as, on its provider's OpenCode provider. */
function aiSubscriptionOpenCodeModel(named: AiSubscriptionModelName): string {
  return `${AI_SUBSCRIPTION_DESCRIPTORS[named.provider].openCodeProviderId}/${named.modelId}`;
}

/**
 * The OpenCode model reference a model of the catalog runs as: a Gateway
 * model's is what sessions store, a subscription model's names its provider's
 * OpenCode provider. Nothing else builds one.
 */
export function openCodeModelOf(entry: CodeSessionCatalogModel): string {
  return entry.route === 'ai_subscription'
    ? aiSubscriptionOpenCodeModel({
        provider: entry.provider,
        modelId: aiSubscriptionModelIdOf(entry),
      })
    : storedModelOf(entry);
}

export type CodeSessionSpeedChoice = {
  provider: AiSubscriptionProvider;
  speed: CodeSessionSpeed;
  /**
   * The stored model at each speed the session may be set to, at the effort it names. No `fast`
   * where the descriptor has no `fastTier`, though a session already fast stays fast until it is
   * set to standard.
   */
  models: Readonly<{ standard: string; fast?: string }>;
};

const SPEED_CHOICES: ReadonlyMap<string, CodeSessionSpeedChoice> = new Map(
  CODE_SESSION_AI_SUBSCRIPTION_MODELS.map((entry) => [
    storedModelOf(entry),
    {
      provider: entry.provider,
      speed: entry.speed,
      models: {
        standard: storedModelOf({ ...entry, speed: 'standard' as const }),
        ...(entry.fastTier ? { fast: storedModelOf({ ...entry, speed: 'fast' as const }) } : {}),
      },
    },
  ]),
);

export function codeSessionSpeedOf(model: string): CodeSessionSpeedChoice | undefined {
  const { model: named, effort } = splitStoredModelEffort(model);
  const choice = SPEED_CHOICES.get(named);
  if (choice === undefined || effort === undefined) {
    return choice;
  }
  const { standard, fast } = choice.models;
  return {
    ...choice,
    models: {
      standard: storedWithEffort(standard, effort),
      ...(fast === undefined ? {} : { fast: storedWithEffort(fast, effort) }),
    },
  };
}

/** The subscription catalog's entry for `named`, a stored model without its effort. */
function aiSubscriptionEntryOf(named: string): CodeSessionAiSubscriptionModel | undefined {
  return CODE_SESSION_AI_SUBSCRIPTION_MODELS.find((entry) => storedModelOf(entry) === named);
}

/** The Gateway catalog's entry for `named`, a stored model without its effort. */
function routedEntryOf(named: string): CodeSessionModel | undefined {
  return CODE_SESSION_ROUTED_MODELS.find((entry) => storedModelOf(entry) === named);
}

/** A stored model's effort, and the efforts a new session on its model may run at. */
export type CodeSessionEffortChoice = {
  /** The effort the session runs at: the one its stored model names, or its model's default. */
  effort: CodeSessionReasoningEffort;
  /** The model's catalog effort, which a stored model at that effort leaves unnamed. */
  defaultEffort: CodeSessionReasoningEffort;
  /** From low to max, `defaultEffort` among them. */
  efforts: readonly CodeSessionReasoningEffort[];
};

/**
 * The effort choice of a stored model of the catalog, a picker model or a subscription model at
 * either speed. Undefined for a model the catalog no longer lists.
 */
export function codeSessionEffortOf(model: string): CodeSessionEffortChoice | undefined {
  const { model: named, effort } = splitStoredModelEffort(model);
  const entry = isAiSubscriptionModel(named) ? aiSubscriptionEntryOf(named) : routedEntryOf(named);
  return (
    entry && {
      effort: effort ?? entry.reasoningEffort,
      defaultEffort: entry.reasoningEffort,
      efforts: entry.offersEveryEffort ? CODE_SESSION_REASONING_EFFORTS : [entry.reasoningEffort],
    }
  );
}

/**
 * `model`, as stored, at `effort`, which replaces any effort it names. It names none for no effort,
 * for its model's default, and for an effort its model doesn't offer. So each choice is stored one
 * way, and a start at the default claims the session warmed for the model; an effort carried to
 * another model gives way to that model's default where it isn't offered; and an effort the person
 * never changed, which their stored model leaves unnamed, doesn't carry.
 */
export function withReasoningEffort(
  model: string,
  effort: CodeSessionReasoningEffort | undefined,
): string {
  const { model: named } = splitStoredModelEffort(model);
  const choice = codeSessionEffortOf(named);
  return effort === undefined ||
    effort === choice?.defaultEffort ||
    choice?.efforts.includes(effort) !== true
    ? named
    : storedWithEffort(named, effort);
}

/**
 * Where a session on a model that cannot read images or PDFs moves when its
 * prompt carries one: the composer offers the switch, and a Slack session,
 * where no one picks a model, starts here when the default cannot read them.
 */
export const CODE_SESSION_DEFAULT_IMAGE_MODEL = storedModelOf(CODE_SESSION_ANTHROPIC_MODELS[0]);

/** The OpenCode agent an interactive session runs as. */
export const CODE_SESSION_OPENCODE_AGENT = 'cubic';

/**
 * The curated Gateway entries, in picker order. Runtime lookups keep using
 * `CODE_SESSION_ROUTED_MODELS`, including entries hidden from new-session choices.
 */
export const CODE_SESSION_OFFERED_GATEWAY_MODELS =
  CODE_SESSION_ROUTED_MODELS.filter(isOfferedCodeSessionModel);

/**
 * The models offered for a new session, as sessions store them, in the
 * picker's order: each routed model not hidden from the picker. The picker
 * lists these, and cubic's MCP `start_session` accepts only these.
 */
export const CODE_SESSION_OFFERED_MODELS = [
  ...CODE_SESSION_OFFERED_GATEWAY_MODELS.map((entry) => storedModelOf(entry)),
] as const;

export type CodeSessionOfferedModel = (typeof CODE_SESSION_OFFERED_MODELS)[number];

// Keep the interactive default separate from review/auto-fix harness defaults.
export const CODE_SESSION_DEFAULT_MODEL = storedModelOf(CODE_SESSION_GPT_61_SOL_MODEL);

/** What a request to start a session on a model `isStartableCodeSessionModel` refuses is told. */
export const UNOFFERED_MODEL_MESSAGE = 'This model isn’t offered.';

/**
 * Whether a person may ask the web app for a new session on `model`: a Gateway
 * model, or a subscription model the catalog still supports at that speed.
 * Gateway models pass whether the picker offers them or not, since a desktop
 * window on an old bundle may send one the picker has since dropped.
 * Hidden catalog entries stay startable for older bundles and inherited sessions.
 * Whether their subscription can run a
 * subscription model is `assertAiSubscriptionSessionStart`'s to say. A model
 * naming an effort passes only where its catalog entry offers that effort
 * (`codeSessionEffortOf`), and one with a suffix naming no effort never does.
 */
export function isStartableCodeSessionModel(model: string): boolean {
  const { model: named, effort } = splitStoredModelEffort(model);
  if (
    named.includes(REASONING_EFFORT_SEPARATOR) ||
    (effort !== undefined && codeSessionEffortOf(model)?.efforts.includes(effort) !== true)
  ) {
    return false;
  }
  if (!isAiSubscriptionModel(named)) {
    return named.startsWith(OPENCODE_GATEWAY_PROVIDER_PREFIX);
  }
  const speedChoice = codeSessionSpeedOf(named);
  return speedChoice?.models[speedChoice.speed] === named;
}

/** How a session's stored model runs. */
export type CodeSessionRun = {
  /** The OpenCode agent the session is created as. */
  agent: typeof CODE_SESSION_OPENCODE_AGENT;
  /**
   * The OpenCode model reference the agent runs: `vercel/<gateway model id>`
   * on the Gateway, `<the provider's OpenCode provider>/<provider model id>`
   * on a subscription.
   */
  openCodeModel: string;
} & (
  | {
      inference: 'gateway';
      /**
       * Routing, the session's effort and image support; undefined for a model the catalog does
       * not support.
       */
      model: CodeSessionModel | undefined;
    }
  | {
      inference: 'ai_subscription';
      /** Unknown providers remain on the subscription route and fail rather than spending cubic's key. */
      provider: AiSubscriptionProvider | undefined;
      /**
       * The session's effort and image support are also undefined for a subscription model since
       * removed from the catalog. Its inference route remains known without that entry.
       */
      model: CodeSessionAiSubscriptionModel | undefined;
    }
);

/** `entry` at the effort a stored model names, or at its catalog effort for none. */
function atEffort<Entry extends CodeSessionCatalogModel>(
  entry: Entry | undefined,
  effort: CodeSessionReasoningEffort | undefined,
): Entry | undefined {
  return entry === undefined || effort === undefined
    ? entry
    : { ...entry, reasoningEffort: effort };
}

/**
 * How a session on `model`, as stored on the session, runs. Everything that
 * hands the stored model to OpenCode or the sandbox goes through this, since
 * a subscription model's stored name is not its OpenCode model. A subscription
 * model naming a provider this build doesn't know runs as itself and fails
 * rather than running elsewhere. A stored model
 * naming an effort runs its model at that effort: its entry carries the effort,
 * and its OpenCode model is the model alone.
 */
export function resolveCodeSessionModel(model: string): CodeSessionRun {
  const { model: named, effort } = splitStoredModelEffort(model);
  if (isAiSubscriptionModel(named)) {
    const subscription = aiSubscriptionModelOf(named);
    return {
      inference: 'ai_subscription',
      provider: subscription?.provider,
      agent: CODE_SESSION_OPENCODE_AGENT,
      openCodeModel: subscription ? aiSubscriptionOpenCodeModel(subscription) : named,
      model: atEffort(aiSubscriptionEntryOf(named), effort),
    };
  }
  return {
    inference: 'gateway',
    agent: CODE_SESSION_OPENCODE_AGENT,
    // Without the effort: cubic's own provider options carry it to every request.
    openCodeModel: named,
    model: atEffort(routedEntryOf(named), effort),
  };
}

/**
 * The catalog entry a session on `model`, as stored on the session, runs as,
 * of either route. Undefined for a model no longer in
 * the catalog.
 */
export function codeSessionModelEntry(model: string): CodeSessionCatalogModel | undefined {
  return resolveCodeSessionModel(model).model;
}

/**
 * Whether a session on `model`, as stored on the session, can be sent images
 * and PDFs. A model no longer supported by the catalog
 * counts as not.
 */
export function codeSessionModelAcceptsImages(model: string): boolean {
  return codeSessionModelEntry(model)?.acceptsImages === true;
}
